import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import {
  Button,
  Dimmer,
  Form,
  Header,
  Icon,
  Loader,
  Message,
  Modal,
} from 'semantic-ui-react';

import { API, FormErrorHandler, Logger } from '../../services';

class Create extends Component {
  /**
   * @property {Objact}
   */
  state = {
    clients: [],
    input: {},
    loading: false,
    message: {
      field: {},
    },
    options: {
      clients: [],
    },
    processing: {},
    search: {},
  };

  /**
   * @param  {Readonly<Object>}  props
   * @constructs Create
   */
  constructor(props) {
    super(props);
    this.handleDismissErrorMessage = this.messageDismissedListener('error').bind(this);
    this.handleDismissSuccessMessage = this.messageDismissedListener('success').bind(this);
    this.handleDropDownChange = this.dropdownChangeListener.bind(this);
    this.handleInputArrayChange = index => _.bindKey(this, 'handleInputChange', _, _, index);
    this.handleInputChange = this.inputChangeListener.bind(this);
    this.handleMount = this.mountListener.bind(this);
    this.handleReset = this.resetListener.bind(this);
    this.handleSubmit = this.submitListener.bind(this);
    this.handleUnmount = this.unmountListener.bind(this);
  }

  /**
   * 
   * @param {React.ChangeEvent} event 
   * @param {import('semantic-ui-react').DropdownProps} data 
   */
  dropdownChangeListener(event, { name, value }) {
    this.setState({
      input: {
        ...this.state.input,
        [name]: value,
      },
    });
  }

  /**
   * @param  {React.ChangeEvent}  event
   * @param  {InputOnChangeData}  data
   * @param  {String}  data.name
   * @param  {number}  index
   * @param  {any}  data.value
   */
  inputChangeListener(event, { name, value }, index) {
    const { input } = this.state;
    Logger.debug(`Indexed: ${index}`);
    if (index !== undefined) {
      input[name][index] = value;
    } else {
      input[name] = value;
    }
    this.setState({ input });
  }

  /**
   * @param  {String} name
   *
   * @return {Function(event: React.MouseEvent): void}
   */
  messageDismissedListener(name) {
    return (event) => {
      const { message } = this.state;
      message[name] = null;
      this.setState({
        message: {
          ...this.state.message,
          [name]: null,
        },
      });
    };
  }

  /**
   * @param  {null}  nothing
   * @param  {ModalProps}  data
   * 
   * @return {void}
   */
  mountListener(nothing, data) {
    if (!this.state.loading) {
      this.setState({
        loading: true,
      });
      API
        .get('/api/v1/clients')
        .then((response) => {
          this.setState({
            clients: response.data.map(
              (client, index) => ({
                key: index,
                text: client.name,
                value: client.id,
              })
            ),
          });
        })
        .catch((reason) => {
          this.setState({
            message: {
              ...this.state.message,
              error: 'retrieve',
            },
          });
        })
        .finally(() => {
          this.setState({
            loading: false,
          });
        });
    }
  }

  /**
   * @return {void}
   */
  onClientError() {
    this.setState({
      message: {
        error: 'client',
        success: null,
        field: {},
      },
    });
  }

  /**
   * @param  {Object}  errors
   * 
   * @return {void}
   */
  onFieldError(errors) {
    this.setState({
      message: {
        error: 'fields',
        success: null,
        field: errors,
      },
    });
  }

  /**
   * @return {void}
   */
  onServerError() {
    this.setState({
      message: {
        error: 'server',
        success: null,
        field: {},
      },
    });
  }

  /**
   * @return {void}
   */
  onUnknownError() {
    this.setState({
      message: {
        error: 'unexpected',
        success: null,
        field: {},
      },
    });
  }

  /**
   * @param  {React.FormEvent}  event
   * 
   * @return {void}
   */
  resetListener(event) {
    event.preventDefault();
    this.setState({
      input: {},
    });
  }

  /**
   * @param  {React.FormEvent}  event
   * 
   * @return {void}
   */
  submitListener(event) {
    event.preventDefault();
    this.setState({
      loading: true,
    });
    API
      .post('/api/v1/projects', this.state.input)
      .then((reponse) => {
        this.setState({
          input: {
            name: null,
            users: [null],
          },
          message: {
            error: null,
            field: {},
            success: 'create',
          },
        });
      })
      .catch((reason) => {
        FormErrorHandler(
          reason,
          {
            onClientError: this.onClientError.bind(this),
            onFieldError: this.onFieldError.bind(this),
            onServerError: this.onServerError.bind(this),
            onUnknownError: this.onUnknownError.bind(this),
          }
        );
      })
      .finally(() => {
        this.setState({
          loading: false,
        });
      });
  }

  /**
   * @param  {null}  nothing
   * @param  {SemanticUI.ModalProps}  data
   * 
   * @return {void}
   */
  unmountListener(nothing, data) {
    this.setState({
      clients: [],
      input: {},
      message: {
        field: {},
      },
    });
  }

  /**
   * @return {React.ReactNode}
   */
  render() {
    const { onClose, open, t } = this.props;
    return (
      <Modal
        as={Form}
        closeIcon={!this.state.loading}
        closeOnDimmerClick={!this.state.loading}
        closeOnDocumentClick={!this.state.loading}
        error={Boolean(this.state.message.error)}
        onClose={onClose}
        onMount={this.handleMount}
        onReset={this.handleReset}
        onSubmit={this.handleSubmit}
        onUnmount={this.handleUnmount}
        open={open}
        success={Boolean(this.state.message.success)}
      >
        <Dimmer active={this.state.loading} inverted>
          <Loader />
        </Dimmer>
        <Header icon='add' content={t('title.create')} />
        <Modal.Content scrolling>
          <Message
            content={t(`message.error.${this.state.message.error}.content`)}
            error
            icon='exclamation'
            header={t(`message.error.${this.state.message.error}.title`)}
            onDismiss={this.handleDismissErrorMessage}
          />
          <Message
            content={t(`message.success.${this.state.message.success}.content`)}
            success
            icon='check'
            header={t(`message.success.${this.state.message.success}.title`)}
            onDismiss={this.handleDismissSuccessMessage}
          />
          <Form.Group>
            <Form.Dropdown
              error={
                this.state.message.field.client_id
                && {
                  content: this.state.message.field.client_id.join("\n"),
                  pointing: 'below'
                }
              }
              fluid
              label={t('input.label.client')}
              loading={this.state.processing.reportee_id}
              name="client_id"
              noResultsMessage={t('input.message.noResult')}
              onChange={this.handleDropDownChange}
              options={this.state.options.clients}
              placeholder={t('input.placeholder.client')}
              scrolling
              selection
              search
              value={this.state.input.client_id}
              width={10}
            />
            <Form.Input
              type="number"
              error={
                this.state.message.field.year
                && {
                  content: this.state.message.field.year.join("\n"),
                  pointing: 'below',
                }
              }
              label={t('input.label.year')}
              max={(new Date()).getFullYear()}
              min={1970}
              name="year"
              onChange={this.handleInputChange}
              placeholder={t('input.placeholder.year')}
              value={this.state.input.year}
              width={6}
            />
          </Form.Group>
        </Modal.Content>
        <Modal.Actions>
          <Button color='red' onClick={onClose} type="reset">
            <Icon name='remove' /> {t('button.cancel')}
          </Button>
          <Button color='blue' type="reset">
            <Icon name='refresh' /> {t('button.reset')}
          </Button>
          <Button color='green' type="submit">
            <Icon name='save' /> {t('button.save')}
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

/**
 * @static {Object}
 */
Create.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default withTranslation('projects')(Create);
